import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { PostChatAudioRequest, PostChatMessage, PostContext, PostChatStatusEvent } from '../types/chat';
import { API_URL } from '../config/api';

export class PostChatService {
  private connection: HubConnection | null = null;
  private isConnected = false;
  private isReconnecting = false; // Флаг для отслеживания переподключения
  
  // Простые обработчики событий
  private messageHandlers: ((message: PostChatMessage) => void)[] = [];
  private statusHandlers: ((status: PostChatStatusEvent) => void)[] = [];
  private errorHandlers: ((error: string) => void)[] = [];
  private messageDeletedHandlers: ((messageId: string) => void)[] = [];
  private chatClearedHandlers: ((data: { contentPlanId: string; postId?: string }) => void)[] = [];
  private reconnectedHandlers: (() => void)[] = []; // Обработчики переподключения
  private disconnectedHandlers: (() => void)[] = []; // Обработчики отключения

  constructor() {
    this.initializeConnection();
  }

  private initializeConnection() {
    const token = localStorage.getItem('auth_token');
    const apiUrl = API_URL;
    // Убираем /api из URL для SignalR хабов
    const hubUrl = `${apiUrl.replace('/api', '')}/postChatHub`;
    
    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token || ''
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.connection) return;

    // Обработчик получения сообщений
    this.connection.on('MessageReceived', (data: any) => {
      // Преобразуем данные с сервера в формат PostChatMessage
      const message: PostChatMessage = {
        id: data.Id || data.id || data.MessageId || Date.now().toString(),
        content: data.Content || data.content || data.Message || '',
        messageType: data.MessageType || data.messageType,
        audioUrl: data.AudioUrl || data.audioUrl,
        imageUrls: (data.ImageUrls || data.imageUrls) ?? null,
        durationMs: data.DurationMs ?? data.durationMs,
        isFromAI: data.IsFromAI !== undefined ? data.IsFromAI : (data.isFromAI !== undefined ? data.isFromAI : false),
        isFromCurrentUser: data.IsFromCurrentUser !== undefined ? data.IsFromCurrentUser : (data.isFromCurrentUser !== undefined ? data.isFromCurrentUser : false),
        timestamp: data.Timestamp || data.timestamp || data.CreatedAt || new Date().toISOString()
      };
      
      // Проверяем, что сообщение содержит необходимые данные
      if (!message.content && !message.id) {
        return;
      }
      
      this.messageHandlers.forEach(handler => handler(message));
    });

    // Обработчик изменения статуса
    this.connection.on('StatusChanged', (status: PostChatStatusEvent) => {
      this.statusHandlers.forEach(handler => handler(status));
    });

    // Обработчик ошибок
    this.connection.on('Error', (error: string) => {
      this.errorHandlers.forEach(handler => handler(error));
    });

    // Обработчик очистки истории
    this.connection.on('ChatHistoryCleared', (data: { ContentPlanId: string; PostId?: string }) => {
      this.chatClearedHandlers.forEach(handler => handler({
        contentPlanId: data.ContentPlanId,
        postId: data.PostId
      }));
    });

    // Обработчик удаления отдельного сообщения
    this.connection.on('MessageDeleted', (data: { messageId: string } | { MessageId: string }) => {
      // Поддерживаем оба варианта именования полей для совместимости
      const messageId = (data as any).messageId || (data as any).MessageId;
      
      if (messageId) {
        this.messageDeletedHandlers.forEach(handler => handler(messageId));
      }
    });

    // Обработчики событий соединения
    this.connection.onreconnecting(() => {
      this.isReconnecting = true;
    });

    this.connection.onreconnected(() => {
      this.isReconnecting = false;
      this.reconnectedHandlers.forEach(handler => handler());
    });

    this.connection.onclose(() => {
      this.isConnected = false;
      this.isReconnecting = false;
      this.disconnectedHandlers.forEach(handler => handler());
    });
  }

  // Подключение к SignalR Hub
  public async connect(): Promise<void> {
    if (this.isConnected || !this.connection) return;

    try {
      await this.connection.start();
      this.isConnected = true;
    } catch (error) {
      this.isConnected = false;
      throw error;
    }
  }

  // Отключение от SignalR Hub
  public async disconnect(): Promise<void> {
    if (!this.isConnected || !this.connection) return;

    try {
      await this.connection.stop();
      this.isConnected = false;
    } catch (error) {
      // Игнорируем ошибки отключения
    }
  }

  // Отправка сообщения
  public async sendMessage(
    contentPlanId: string, 
    postId: string | undefined, 
    message: string, 
    postContext: PostContext,
    imageUrls?: string[] | null
  ): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    try {
      // 5-й параметр (imageUrls) поддерживается на бэкенде, но держим его опциональным для совместимости
      await this.connection.invoke('SendMessage', contentPlanId, postId, message, postContext, imageUrls ?? null);
    } catch (error) {
      throw error;
    }
  }

  // Регенерация ответа ИИ по сообщению ИИ (без повторной отправки user-сообщения)
  public async regenerateMessage(messageId: string, postContext: PostContext): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    await this.connection.invoke('RegenerateMessage', messageId, postContext);
  }

  public async sendAudioMessage(
    contentPlanId: string,
    postId: string | undefined,
    audio: Omit<PostChatAudioRequest, 'contentPlanId' | 'postId' | 'postContext'>,
    postContext: PostContext
  ): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    const payload: any = {
      contentPlanId,
      postId,
      audioKey: audio.audioKey,
      audioUrl: audio.audioUrl,
      audioMimeType: audio.audioMimeType,
      durationMs: audio.durationMs
    };

    await this.connection.invoke('SendAudioMessage', contentPlanId, postId, payload, postContext);
  }

  // Очистка истории чата
  public async clearChatHistory(contentPlanId: string, postId?: string): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    try {
      await this.connection.invoke('ClearChatHistory', contentPlanId, postId);
    } catch (error) {
      throw error;
    }
  }

  // Получение истории чата
  public async getChatHistory(contentPlanId: string, postId?: string): Promise<PostChatMessage[]> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    try {
      // ВАЖНО: Убеждаемся что undefined или "undefined" передается как null
      const normalizedPostId = postId === undefined || postId === null || postId === "undefined" ? null : postId;
      
      const history = await this.connection.invoke('GetChatHistory', contentPlanId, normalizedPostId);
      
      if (!history || !Array.isArray(history)) {
        return [];
      }

      // Преобразуем данные истории в правильный формат
      const formattedHistory: PostChatMessage[] = history.map((item: any) => {
        const message: PostChatMessage = {
          id: item.Id || item.id || item.MessageId || Date.now().toString(),
          content: item.Content || item.content || item.Message || '',
          messageType: item.MessageType || item.messageType,
          audioUrl: item.AudioUrl || item.audioUrl,
          imageUrls: (item.ImageUrls || item.imageUrls) ?? null,
          durationMs: item.DurationMs ?? item.durationMs,
          isFromAI: item.IsFromAI !== undefined ? item.IsFromAI : (item.isFromAI !== undefined ? item.isFromAI : false),
          isFromCurrentUser: item.IsFromCurrentUser !== undefined ? item.IsFromCurrentUser : (item.isFromCurrentUser !== undefined ? item.isFromCurrentUser : false),
          timestamp: item.Timestamp || item.timestamp || item.CreatedAt || new Date().toISOString()
        };
        
        return message;
      });
      
      return formattedHistory;
    } catch (error) {
      throw error;
    }
  }

  // Удаление отдельного сообщения
  public async deleteMessage(messageId: string): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    try {
      await this.connection.invoke('DeleteMessage', messageId);
    } catch (error) {
      throw error;
    }
  }

  // Обновление содержимого сообщения (сохранение на бэкенде)
  public async updateMessageContent(messageId: string, content: string): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    try {
      await this.connection.invoke('UpdateMessageContent', messageId, content);
    } catch (error) {
      throw error;
    }
  }

  // Обновление PostId в сообщениях (для замены временного ID на реальный)
  public async updatePostIdInMessages(contentPlanId: string, oldPostId: string, newPostId: string): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to post chat hub');
    }

    try {
      await this.connection.invoke('UpdatePostIdInMessages', contentPlanId, oldPostId, newPostId);
    } catch (error) {
      throw error;
    }
  }

  // Подписка на события
  public onMessageReceived(handler: (message: PostChatMessage) => void): () => void {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  public onStatusChanged(handler: (status: PostChatStatusEvent) => void): () => void {
    this.statusHandlers.push(handler);
    return () => {
      const index = this.statusHandlers.indexOf(handler);
      if (index > -1) {
        this.statusHandlers.splice(index, 1);
      }
    };
  }

  public onError(handler: (error: string) => void): () => void {
    this.errorHandlers.push(handler);
    return () => {
      const index = this.errorHandlers.indexOf(handler);
      if (index > -1) {
        this.errorHandlers.splice(index, 1);
      }
    };
  }

  public onMessageDeleted(handler: (messageId: string) => void): () => void {
    this.messageDeletedHandlers.push(handler);
    return () => {
      const index = this.messageDeletedHandlers.indexOf(handler);
      if (index > -1) {
        this.messageDeletedHandlers.splice(index, 1);
      }
    };
  }

  public onChatCleared(handler: (data: { contentPlanId: string; postId?: string }) => void): () => void {
    this.chatClearedHandlers.push(handler);
    return () => {
      const index = this.chatClearedHandlers.indexOf(handler);
      if (index > -1) {
        this.chatClearedHandlers.splice(index, 1);
      }
    };
  }

  public onReconnected(handler: () => void): () => void {
    this.reconnectedHandlers.push(handler);
    return () => {
      const index = this.reconnectedHandlers.indexOf(handler);
      if (index > -1) {
        this.reconnectedHandlers.splice(index, 1);
      }
    };
  }

  public onDisconnected(handler: () => void): () => void {
    this.disconnectedHandlers.push(handler);
    return () => {
      const index = this.disconnectedHandlers.indexOf(handler);
      if (index > -1) {
        this.disconnectedHandlers.splice(index, 1);
      }
    };
  }

  public get connected(): boolean {
    return this.isConnected;
  }

  public get reconnecting(): boolean {
    return this.isReconnecting;
  }
} 